import { useEffect, useMemo } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { Link } from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { Image } from '@tiptap/extension-image'
import { Youtube } from '@tiptap/extension-youtube'
import { cn } from '@/lib/utils'
import { Toolbar } from './Toolbar'
import { ParagraphSelector } from './extensions/paragraph-selector'

export function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  editable = true,
  className,
  aiPanelOpen = false,
  onParagraphSelect,
  onEditorReady,
}) {
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3],
      },
      codeBlock: false,
      code: false,
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'text-primary underline underline-offset-4',
      },
    }),
    Table.configure({
      resizable: true,
      HTMLAttributes: {
        class: 'border-collapse w-full',
      },
    }),
    TableRow,
    TableCell.configure({
      HTMLAttributes: {
        class: 'border border-border p-2',
      },
    }),
    TableHeader.configure({
      HTMLAttributes: {
        class: 'border border-border p-2 bg-muted font-semibold text-left',
      },
    }),
    Image.configure({
      HTMLAttributes: {
        class: 'max-w-full h-auto rounded-md',
      },
    }),
    Youtube.configure({
      width: 640,
      height: 360,
      nocookie: true,
      HTMLAttributes: {
        class: 'w-full aspect-video rounded-md',
      },
    }),
    ParagraphSelector.configure({
      onParagraphSelect,
    }),
  ], [onParagraphSelect])

  const editor = useEditor({
    extensions,
    content: value,
    editable,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-zinc dark:prose-invert max-w-none',
          'min-h-[200px] px-4 py-3 focus:outline-none',
          'prose-headings:font-semibold',
          'prose-a:text-primary prose-a:underline prose-a:underline-offset-4',
          'prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground',
        ),
      },
    },
  })

  // Expose editor instance to parent
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor)
    }
  }, [editor, onEditorReady])

  // Sync editable state when prop changes (editor is created once, so we need this)
  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable)
    }
  }, [editor, editable])

  // Toggle paragraph selector active state when AI panel opens/closes
  useEffect(() => {
    if (editor) {
      editor.commands.setParagraphSelectorActive(aiPanelOpen)
    }
  }, [editor, aiPanelOpen])

  // Update content when value prop changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])

  // Call onChange when editor content updates
  useEffect(() => {
    if (editor && onChange) {
      const handleUpdate = () => {
        onChange(editor.getHTML())
      }
      editor.on('update', handleUpdate)
      return () => editor.off('update', handleUpdate)
    }
  }, [editor, onChange])

  if (!editor) {
    return (
      <div className={cn('rounded-md border bg-background', className)}>
        <div className="h-10 border-b bg-muted/30 animate-pulse" />
        <div className="min-h-[200px] p-4">
          <div className="h-4 w-3/4 bg-muted/30 rounded animate-pulse" />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-md border bg-background',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        'transition-shadow',
        className
      )}
    >
      {editable && <Toolbar editor={editor} />}
      <EditorContent
        editor={editor}
        placeholder={placeholder}
      />
    </div>
  )
}
